import { setUrlParams, urlCombine } from '../utils/url';
import {
  ChatAiCompletionRequest,
  ChatAiCompletionResponse,
} from './dto/completionRequest';
import { SemanticModel } from './dto/semanticModel';
import { ToolCallRequest, ToolCallResponse } from './dto/tools';
import { IChatAiModelService } from './iChatAiModelService';
import { FeedbackPayload, FeedbackResponse } from './dto/feedback';

export interface ChatCompletionResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: {
    index: number;
    message: {
      role: string;
      content: string | null;
      tool_calls: {
        id: string;
        type: string;
        function: {
          name: string;
          arguments: string; // JSON string containing query, e.g., '{"query": "SELECT * FROM table"}'
        };
      }[];
    };
    finish_reason: string;
  }[];
  completion_id: string; // Unique ID for the completion
}

// New interface for the second API call response
export interface ExecuteCompletionResponse {
  status: string;
  data: {
    query_results: any[]; // Array of results from the executed query
    execution_time: string; // e.g., "0.5s"
  };
  completion_id: string;
}

export class ChatAiModelService implements IChatAiModelService {
  _baseApiUrl: string;
  _baseHttpHeaders: { Authorization: string; Accept: string };

  constructor(apiUrl: string, accessToken: string) {
    this._baseApiUrl = apiUrl;

    this._baseHttpHeaders = {
      Authorization: 'Bearer ' + accessToken,
      Accept: 'application/json',
    };
  }
  async executeTool(request: ToolCallRequest): Promise<ToolCallResponse> {
    const url = urlCombine(this._baseApiUrl, '/v0/tool/invoke');

    // Special handling for tool calls to preserve metadata
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(request, null, 2),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    // Return the full response to preserve metadata
    return {
      data: data.data,
      metadata: data.metadata
    };
  }

  async chatCompletion(
    chatCompletion: ChatAiCompletionRequest
  ): Promise<ChatAiCompletionResponse> {
    const url = urlCombine(this._baseApiUrl, '/v0/chat/completions');
    const response = await this.executeRequest<ChatAiCompletionResponse>(url, {
      method: 'POST',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(chatCompletion, null, 2),
    });
    // The new API returns the response directly, not wrapped in a data object
    return response;
  }

  async getSemanticModels(accountName: string): Promise<SemanticModel[]> {
    const url = setUrlParams(urlCombine(this._baseApiUrl, '/v0/model/list'), {
      provider: 'snowflake',
      projection: true,
      accountName: accountName,
    });
    const response = await this.executeRequest<SemanticModel[]>(url, {
      headers: this._baseHttpHeaders,
    });
    // The new API returns the array directly, not wrapped in a data object
    return response;
  }

  async getSemanticModelsByDataproduct(
    referenceId: string
  ): Promise<SemanticModel[]> {
    const url = setUrlParams(
      urlCombine(this._baseApiUrl, `/v0/model/reference/${referenceId}`),
      { provider: 'snowflake', projection: true }
    );
    const response = await this.executeRequest<SemanticModel[]>(url, {
      headers: this._baseHttpHeaders,
    });
    // The new API returns the array directly, not wrapped in a data object
    return response;
  }

  async createSemanticModel(
    chatAiModel: SemanticModel
  ): Promise<SemanticModel> {
    const url = urlCombine(this._baseApiUrl, '/v0/model');
    const response = await this.executeRequest<SemanticModel>(url, {
      method: 'POST',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(chatAiModel, null, 2),
    });
    // The new API returns the response directly, not wrapped in a data object
    return response;
  }

  async registerSemanticModel(
    chatAiModel: SemanticModel
  ): Promise<SemanticModel> {
    const url = urlCombine(this._baseApiUrl, '/v0/model:register');
    const response = await this.executeRequest<SemanticModel>(url, {
      method: 'POST',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(chatAiModel, null, 2),
    });
    return response;
  }

  async updateSemanticModel(
    chatAiModel: SemanticModel
  ): Promise<SemanticModel> {
    const url = urlCombine(this._baseApiUrl, `/v0/model/${chatAiModel.id}`);
    const response = await this.executeRequest<SemanticModel>(url, {
      method: 'PUT',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(chatAiModel, null, 2),
    });
    // The new API returns the response directly, not wrapped in a data object
    return response;
  }

  async getSemanticModel(chatAiModelId: string): Promise<SemanticModel> {
    const url = urlCombine(this._baseApiUrl, `/v0/model/${chatAiModelId}`);
    const response = await this.executeRequest<SemanticModel>(url, {
      method: 'GET',
      headers: this._baseHttpHeaders,
    });
    // The new API returns the response directly, not wrapped in a data object
    return response;
  }

  async deleteSemanticModel(chatAiModelId: string): Promise<void> {
    const url = urlCombine(this._baseApiUrl, `/v0/model/${chatAiModelId}`);
    await this.executeRequest<SemanticModel[]>(url, {
      method: 'DELETE',
      headers: this._baseHttpHeaders,
    });
  }

  async getApiVersion(): Promise<string> {
    const url = urlCombine(this._baseApiUrl, '/healthcheck');
    return await fetch(url, {
      method: 'GET',
      headers: this._baseHttpHeaders,
    }).then(async (response) => {
      const data = (await response.json()) as HealthCheckResponse;

      if (response.ok) return data.data.State.currentApiVersion;

      return Promise.reject<string>();
    });
  }

  async submitChatFeedback(
    payload: FeedbackPayload
  ): Promise<FeedbackResponse> {
    const url = urlCombine(this._baseApiUrl, '/v0/chat/feedback');
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...this._baseHttpHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload, null, 2),
    });
    const data = await response.json();
    if (response.ok) {
      // Normalize both shapes (wrapped and unwrapped)
      if (data?.status === 'success' && data?.data)
        return data as FeedbackResponse;
      if (data?.feedback_id && data?.message) {
        return {
          status: 'success',
          data: { feedback_id: data.feedback_id, message: data.message },
        };
      }
      // Fallback
      return { status: 'success', data: data } as FeedbackResponse;
    }
    // Throw the raw error to be caught by upstream handler
    throw data;
  }

  // Updated executeRequest to handle new response structure
  private executeRequest<T>(url: string, init: RequestInit): Promise<T> {
    return fetch(url, init).then(async (response) => {
      const data = await response.json();

      if (response.ok) {
        // New API structure - response is returned directly
        // Handle both old structure (data.data) and new structure (data)
        return (data.data || data) as T;
      }

      // Error case - throw the raw API error object
      throw data;
    });
  }

  /**
   * Executes the tools associated with a given completion ID.
   *
   * @param {string} completionId The unique ID of the completion to execute.
   * @param {string} accessToken The access token for authorization.
   * @returns {Promise<ExecuteCompletionResponse>} A promise that resolves with the execution results.
   */
  async executeCompletion(
    request: ChatAiCompletionRequest
  ): Promise<{ data: any[]; metadata?: any; trace_id: string; correlation_id?: string }> {
    const url = `${this._baseApiUrl}/v0/chat/completions/execute`;

    // Special handling for executeCompletion to preserve metadata
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...this._baseHttpHeaders,
        'Content-Type': 'application/json;charset=UTF-8',
      },
      body: JSON.stringify(request, null, 2),
    });

    const data = await response.json();

    if (!response.ok) {
      throw data;
    }

    // Return the full response to preserve metadata
    return {
      data: data.data || data,
      metadata: data.metadata, // Preserve metadata for table formatting
      trace_id: data.trace_id,
      correlation_id: data.correlation_id,
    };
  }
}

interface HealthCheckResponse {
  data: {
    State: {
      currentApiVersion: string;
    };
  };
  status: string;
}

export type ProblemDetails = {
  status: number;
  title: string;
  detail: string;
  error_type: string;
  trace_id: string;
  correlation_id?: string;
};
